// Community tag voting (see supabase/migrations/20261284000000_tag_proposals.sql).
// Pure helpers shared by the API routes, the modal hook and the tests.

export type ProposalKind = "add" | "remove"
export type ProposalStatus = "pending" | "approved" | "rejected"

export interface TagProposal {
  id: string
  contentId: string
  tag: string
  kind: ProposalKind
  status: ProposalStatus
  reason: string | null
  upvotes: number
  downvotes: number
  /** The signed-in viewer's own vote: 1, -1, or 0 for none. */
  myVote: 1 | -1 | 0
  createdAt: string
  resolvedAt: string | null
}

/** A tag_proposals row as selected by the API routes. */
export interface ProposalRow {
  id: string
  content_id: string
  tag: string
  kind: ProposalKind
  status: ProposalStatus
  reason: string | null
  upvotes: number
  downvotes: number
  created_at: string
  resolved_at: string | null
}

export const PROPOSAL_COLUMNS = "id, content_id, tag, kind, status, reason, upvotes, downvotes, created_at, resolved_at"

export function toProposal(row: ProposalRow, myVote: number): TagProposal {
  return {
    id: row.id,
    contentId: row.content_id,
    tag: row.tag,
    kind: row.kind,
    status: row.status,
    reason: row.reason,
    upvotes: row.upvotes,
    downvotes: row.downvotes,
    myVote: myVote === 1 ? 1 : myVote === -1 ? -1 : 0,
    createdAt: row.created_at,
    resolvedAt: row.resolved_at,
  }
}

export const TAG_STAFF_ROLES = ["admin", "super_admin", "moderator"]
export const MAX_PROPOSALS_PER_DAY = 30
const MAX_TAG_LENGTH = 80

/** "Big  Ears" -> "big_ears". Returns "" for anything unusable. */
export function normalizeProposalTag(raw: unknown): string {
  if (typeof raw !== "string") return ""
  const tag = raw.trim().toLowerCase().replace(/^[+\-]+/, "").replace(/\s+/g, "_")
  if (!tag || tag.length > MAX_TAG_LENGTH) return ""
  // Tags are plain names; refuse markup and control characters outright.
  if (/[<>"\\\u0000-\u001f]/.test(tag)) return ""
  return tag
}

const CONTENT_ID_RE = /^[a-z0-9_.-]{1,40}:.{1,300}$/i

export function isValidContentId(value: unknown): value is string {
  return typeof value === "string" && CONTENT_ID_RE.test(value)
}

/**
 * What the community has decided about a post's tags. For each tag the most
 * recently approved proposal wins, so a tag that was approved-removed and later
 * approved-added again shows up again (and the other way round).
 */
export function resolveTagOverlay(proposals: Pick<TagProposal, "tag" | "kind" | "status" | "resolvedAt">[]): {
  added: string[]
  hidden: string[]
} {
  const latest = new Map<string, { kind: ProposalKind; at: number }>()
  for (const p of proposals) {
    if (p.status !== "approved") continue
    const at = p.resolvedAt ? Date.parse(p.resolvedAt) : 0
    const prev = latest.get(p.tag)
    if (!prev || at >= prev.at) latest.set(p.tag, { kind: p.kind, at })
  }
  const added: string[] = []
  const hidden: string[] = []
  for (const [tag, { kind }] of latest) (kind === "add" ? added : hidden).push(tag)
  return { added, hidden }
}

/** Apply the overlay to a source post's tag list. */
export function applyTagOverlay(sourceTags: string[], overlay: { added: string[]; hidden: string[] }): string[] {
  const hidden = new Set(overlay.hidden)
  const seen = new Set<string>()
  const out: string[] = []
  for (const tag of [...sourceTags, ...overlay.added]) {
    const key = tag.trim().toLowerCase().replace(/\s+/g, "_")
    if (!key || hidden.has(key) || seen.has(key)) continue
    seen.add(key)
    out.push(tag)
  }
  return out
}
